import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Circle, FileText, Download, RefreshCw, ShieldCheck, UserCheck, Activity,
} from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, CardHead, Btn, Badge, Avatar, Spinner, statusTone } from "@/components/ui-kit";
import api, { docFileUrl } from "@/lib/api";
import VisitReportModal, { reportPdfUrl } from "@/components/VisitReport";
import { ScoreBadge, StarRating } from "@/components/PerfScore";
import { CycleBadge, CycleHistory, cycleText, sessionsLabel, reportSessionText } from "@/components/Cycle";

export default function CareEpisode() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ref, setRef] = useState(null);
  const [reports, setReports] = useState([]);
  const [viewReport, setViewReport] = useState(null);

  const load = useCallback(() => {
    api.get(`/referrals/${id}`).then((r) => setRef(r.data)).catch(() => nav("/referrals"));
    api.get(`/referrals/${id}/visit-reports`).then((r) => setReports(r.data)).catch(() => setReports([]));
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  if (!ref) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;
  const pkg = ref.package || {};
  const o = ref.outcome || {};
  const physio = ref.assigned_physio;

  const rematch = async () => {
    const { data } = await api.post(`/referrals/${id}/rematch`);
    if (data.assigned_physio) toast.success(`Matched with ${data.assigned_physio.name}`);
    else toast("No credentialed physiotherapist available yet");
    load();
  };

  return (
    <>
      <PageHead title={`Case — ${ref.ref_no}`} subtitle={`Clinical treatment order & care episode for ${ref.patient_name}.${ref.cycle > 1 ? ` ${cycleText(ref.cycle)} (continuing order).` : ""}`}
        action={<Btn onClick={() => nav("/referrals")}><ArrowLeft size={16} /> Back</Btn>} />

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-5 items-start">
        <div className="space-y-5">
          {/* Matching / assignment */}
          <Card className="p-6" data-testid="assignment-card">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-semibold flex items-center gap-2"><UserCheck size={18} className="text-teal-600" /> Physiotherapist Assignment</h4>
              <Badge tone={ref.matching_status === "Assigned" ? "emerald" : "amber"}>{ref.matching_status}</Badge>
            </div>
            {physio ? (
              <div className="flex items-center gap-4">
                <Avatar name={physio.name} size={48} src={docFileUrl(physio.photo_doc_id)} />
                <div className="flex-1">
                  <div className="font-semibold text-[14.5px] flex items-center gap-2">{physio.name}
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-[11.5px] font-semibold"><ShieldCheck size={13} /> {physio.credential_status}</span>
                  </div>
                  <div className="text-[12.5px] text-slate-500">{physio.specialty} · {physio.city}{physio.prq_id ? ` · ${physio.prq_id}` : ""}</div>
                </div>
                <div className="text-right" data-testid="care-physio-perf">
                  <ScoreBadge score={ref.physio_perf?.perf_score} isNew={ref.physio_perf?.is_new} />
                  <div className="text-[11px] text-slate-400">performance score</div>
                  <div className="mt-1"><StarRating value={ref.physio_perf?.patient_rating} count={ref.physio_perf?.patient_rating_count} /></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">The matching engine is finding an available credentialed physiotherapist.</p>
                <Btn size="sm" onClick={rematch} data-testid="rematch-button"><RefreshCw size={14} /> Run matching</Btn>
              </div>
            )}
          </Card>

          {/* Milestones */}
          <Card className="p-6">
            <h4 className="font-display font-semibold mb-4">Case Milestones</h4>
            {(ref.milestones || []).map((m, i) => (
              <div key={m.label} className="flex gap-3 py-3 border-b border-dashed border-slate-200 last:border-0" data-testid={`milestone-${i}`}>
                {m.state === "done" ? <CheckCircle2 className="text-teal-600 shrink-0" size={22} />
                  : m.state === "now" ? <div className="w-[22px] h-[22px] rounded-full border-[3px] border-teal-600 shrink-0" />
                  : <Circle className="text-slate-300 shrink-0" size={22} />}
                <div><div className={`font-semibold text-[13.5px] ${m.state === "todo" ? "text-slate-400" : ""}`}>{m.label}</div><div className="text-[12px] text-slate-500">{m.time}</div></div>
              </div>
            ))}
          </Card>

          {/* Treatment progress */}
          <Card className="p-6" data-testid="progress-card">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-display font-semibold flex items-center gap-2"><Activity size={18} className="text-teal-600" /> Treatment Progress</h4>
              <div className="flex gap-1.5 flex-wrap"><CycleBadge cycle={ref.cycle} testid="care-cycle" /><Badge tone={statusTone(ref.status)}>{o.status || ref.status}</Badge></div>
            </div>
            <div className="flex items-center justify-between text-[12.5px] text-slate-500 mb-1"><span data-testid="care-sessions">{sessionsLabel(o)}</span><span>{o.progress || 0}% complete</span></div>
            <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-600 transition-all" style={{ width: `${o.progress || 0}%` }} /></div>
            <div className="mt-3"><CycleHistory h={ref.cycle_history} testid="care-cycles" /></div>
            <p className="text-[12px] text-slate-400 mt-3">Session logs and milestone completion are pushed here by the assigned physiotherapist via PranIQ Pro.</p>
          </Card>

          {/* Home visit reports (pushed by PranIQ Operations) */}
          <Card className="p-6" data-testid="visit-reports-card">
            <h4 className="font-display font-semibold mb-3 flex items-center gap-2"><FileText size={18} className="text-teal-600" /> Home Visit Reports</h4>
            {reports.length === 0 ? (
              <p className="text-[12.5px] text-slate-400" data-testid="visit-reports-empty">No visit reports yet. PranIQ Operations shares the clinician's home visit report here after reviewing each session.</p>
            ) : reports.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2.5 border-b border-dashed border-slate-200 last:border-0 text-[13px]" data-testid={`visit-report-row-${r.id}`}>
                <FileText size={16} className="text-teal-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">Session {reportSessionText(r)} — {r.visit_date}</div>
                  <div className="text-[11.5px] text-slate-400 truncate">{r.clinician_name}{r.visit_start ? ` · ${r.visit_start}${r.visit_end ? `–${r.visit_end}` : ""}` : ""}</div>
                </div>
                <Btn size="sm" onClick={() => setViewReport(r)} data-testid={`visit-report-view-${r.id}`}>View</Btn>
                <Btn size="sm" onClick={() => window.open(reportPdfUrl(r.id), "_blank")} data-testid={`visit-report-pdf-${r.id}`}><Download size={13} /> PDF</Btn>
              </div>
            ))}
          </Card>

          {/* Pain score trend */}
          <Card className="p-6" data-testid="pain-trend-card">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-display font-semibold flex items-center gap-2"><Activity size={18} className="text-teal-600" /> Pain Score Trend</h4>
              {(o.pain_trend || []).length > 0 && (
                <Badge tone={o.pain_trend[o.pain_trend.length - 1] >= 7 ? "rose" : o.pain_trend[o.pain_trend.length - 1] >= 4 ? "amber" : "emerald"}>
                  Latest {o.pain_trend[o.pain_trend.length - 1]}/10
                </Badge>
              )}
            </div>
            {(o.pain_trend || []).length === 0 ? (
              <p className="text-[12.5px] text-slate-400">No pain scores logged yet. Patient check-ins and physiotherapist session logs will appear here.</p>
            ) : (
              <>
                <div className="flex items-end gap-1.5 h-24 mb-1" data-testid="pain-trend-bars">
                  {(o.pain_trend || []).map((s, i) => (
                    <div key={i} title={`Pain ${s}/10`} data-testid={`pain-bar-${i}`}
                      className={`flex-1 rounded-t ${s >= 7 ? "bg-rose-400" : s >= 4 ? "bg-amber-400" : "bg-emerald-400"}`}
                      style={{ height: `${Math.max(8, s * 10)}%` }} />
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 mb-4"><span>Oldest</span><span>Most recent</span></div>
                {(ref.checkins || []).length > 0 && (
                  <div data-testid="pain-checkins">
                    <div className="pq-label mb-1">Recent check-ins</div>
                    {[...(ref.checkins || [])].slice(-5).reverse().map((c) => (
                      <div key={c.id} className="flex items-center gap-3 py-2.5 border-b border-dashed border-slate-100 last:border-0" data-testid={`pain-checkin-${c.id}`}>
                        <Badge tone={c.pain_score >= 7 ? "rose" : c.pain_score >= 4 ? "amber" : "emerald"}>{c.pain_score}/10</Badge>
                        <div className="flex-1 min-w-0">
                          {c.note && <div className="text-[12.5px] text-slate-700 truncate">{c.note}</div>}
                          <div className="text-[11px] text-slate-400">{c.by === "patient" ? "Patient check-in" : "Physio session"} · {c.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>

          <Card className="p-6">
            <h4 className="font-display font-semibold mb-3">Documents</h4>
            {[`Treatment order — ${ref.ref_no}.pdf`, `Treatment plan — ${pkg.name}.pdf`].map((d) => (
              <div key={d} className="flex items-center gap-3 py-2.5 border-b border-dashed border-slate-200 last:border-0 text-[13px]">
                <FileText size={16} className="text-teal-600" /><span className="flex-1">{d}</span>
                <Btn size="sm" onClick={() => toast.success(`${d} downloaded`)}><Download size={13} /> Download</Btn>
              </div>
            ))}
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="p-5">
            <h4 className="font-display font-semibold mb-3">Patient</h4>
            <div className="flex items-center gap-3 mb-3"><Avatar name={ref.patient_name} size={40} /><div><div className="font-semibold text-[13.5px]">{ref.patient_name}</div><div className="text-[12px] text-slate-500">{ref.patient_meta}</div></div></div>
            <Info label="Diagnosis" value={ref.diagnosis_label} />
            <Info label="Treatment Plan" value={pkg.name} />
            <Info label="Sessions" value={`${pkg.sessions || "Not set"} · ${pkg.weeks || "—"} weeks`} />
            <Info label="Ordered by" value={ref.created_by} />
          </Card>

          <Card className="p-5">
            <h4 className="font-display font-semibold mb-3">Assigned Physiotherapist</h4>
            {physio ? (
              <Info label="Therapist" value={physio.name} />
            ) : <p className="text-sm text-slate-500">Matching in progress…</p>}
          </Card>

          <Card>
            <CardHead title="Actions" />
            <button onClick={rematch} className="w-full flex items-center gap-3 px-5 py-3 border-b border-slate-100 text-[13.5px] font-semibold hover:bg-slate-50 text-left"><RefreshCw size={16} /> Refresh Match Status</button>
            <button onClick={() => toast.success("Summary downloaded")} className="w-full flex items-center gap-3 px-5 py-3 text-[13.5px] font-semibold hover:bg-slate-50 text-left"><Download size={16} /> Download Summary</button>
          </Card>
        </div>
      </div>
      {viewReport && <VisitReportModal r={viewReport} onClose={() => setViewReport(null)} />}
    </>
  );
}

function Info({ label, value }) {
  return <div className="flex justify-between py-2 border-b border-dashed border-slate-100 last:border-0 text-[13px]"><span className="text-slate-500">{label}</span><span className="font-semibold">{value}</span></div>;
}
